"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zProfileColor = exports.zFromGpfitPoints = exports.zRatingCenter = exports.Z_RATING_SHORT_DE = exports.Z_RATING_LABEL_DE = exports.Z_PROFILE_MAX_TESTS = exports.Z_AVERAGE_BAND = exports.Z_PROFILE_CAP_DEPTH = exports.Z_PROFILE_TICKS = exports.Z_PROFILE_DOMAIN = void 0;
exports.roundHalfAway = roundHalfAway;
exports.zRating = zRating;
exports.formatSignedNumber = formatSignedNumber;
exports.profileZWithSource = profileZWithSource;
exports.profileZ = profileZ;
exports.profileTextColor = profileTextColor;
exports.zProfileCap = zProfileCap;
exports.buildZProfileLayout = buildZProfileLayout;
// "Einzelprofil" of one child as diverging z-value bars (Gerald, sketch
// 17.10.2026): bars from the 0 line (up = better, down = worse), a green
// band for "Durchschnitt" (−0,25 ≤ z < +0,25 = 5), a vertical colour ray
// (Farbstrahl) of the GPfit points scale along the y axis, and the rating in
// words. Pure functions shared by the web chart and the PDF Ergebnisblatt,
// so both draw the same geometry. See documentation/charts.md "Einzelprofil".
const charts_1 = require("./charts");
const gpfit_colors_1 = require("./gpfit-colors");
const stanine_1 = require("./stanine");
/** y axis of the profile: z −2,5 … +2,5 (ticks every 0,5). */
exports.Z_PROFILE_DOMAIN = [-2.5, 2.5];
exports.Z_PROFILE_TICKS = [
    -2.5, -2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2, 2.5,
];
/**
 * Depth of the zigzag at the end of a capped bar (|z| > 2,5) and of the
 * arrow on the Farbstrahl, in user units.
 */
exports.Z_PROFILE_CAP_DEPTH = 4;
/** Half width of the "Durchschnitt" band around 0 (= 5 points). */
exports.Z_AVERAGE_BAND = 0.25;
/** At most this many tests are drawn as grouped bars; more -> line chart. */
exports.Z_PROFILE_MAX_TESTS = 3;
/** Gerald's exact wording, by rating 1..9 (index 0 = rating 1). */
exports.Z_RATING_LABEL_DE = [
    "Schlechte Leistung",
    "Sehr schwache Leistung",
    "Schwache Leistung",
    "Unterdurchschnittliche Leistung",
    "Durchschnittliche Leistung",
    "Überdurchschnittliche Leistung",
    "Gute Leistung",
    "Sehr gute Leistung",
    "Exzellente Leistung",
];
/** Short form for the bar labels ("Gut +0,9"). */
exports.Z_RATING_SHORT_DE = [
    "Schlecht",
    "Sehr schwach",
    "Schwach",
    "Unterdurchschn.",
    "Durchschnitt",
    "Überdurchschn.",
    "Gut",
    "Sehr gut",
    "Exzellent",
];
/**
 * Round half away from zero to `decimals` (like Intl "halfExpand"). No
 * longer used for z labels (they use roundRatingZ, see formatSignedNumber).
 */
function roundHalfAway(value, decimals) {
    const f = 10 ** decimals;
    return (Math.sign(value) * Math.round(Math.abs(value) * f + 1e-9)) / f;
}
/**
 * Rating 1..9 of a z-value = classic stanine band of the UNROUNDED z
 * (stanineBand in stanine.ts, Gerald's decision 19.10.2026): lower edge
 * inclusive, e.g. −0,25 ≤ z < +0,25 -> 5, +0,25 ≤ z < +0,75 -> 6,
 * z < −1,75 -> 1, z ≥ +1,75 -> 9. Until 10/2026 z was first rounded to
 * 1 decimal half away from zero, which put the exact negative edges one band
 * too low (−0,25 -> 4 instead of 5).
 *
 * Every rating label, rating colour of the legend and `data-rating` of the
 * web chart and the PDF Ergebnisblatt comes from here. The shown number uses
 * formatSignedNumber (roundRatingZ), which cannot contradict the band. Bar
 * length and bar colour use the unrounded z and do not depend on it; the
 * GPfit points (2·z + 5) and the potential sums are not affected.
 */
function zRating(z) {
    return (0, stanine_1.stanineBand)(z);
}
/** z in the middle of a rating band: 1 -> −2,0 … 5 -> 0 … 9 -> +2,0. */
const zRatingCenter = (rating) => (rating - 5) / 2;
exports.zRatingCenter = zRatingCenter;
/**
 * Signed z with a typographic minus: "+0,9", "−2,0", "0,0" (de) or "+0.9"
 * (en). `locale` e.g. "de-AT" / "en-US". Rounded with roundRatingZ, so the
 * shown value always lies in the same stanine band as `value` (1 decimal:
 * half up toward +∞, −0,25 -> "−0,2", +0,25 -> "+0,3"; 2 decimals: nearest,
 * but +0,249 -> "+0,24" just below an edge).
 */
function formatSignedNumber(value, decimals = 1, locale = "de-AT") {
    const rounded = (0, stanine_1.roundRatingZ)(value, decimals);
    const abs = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(Math.abs(rounded));
    if (rounded === 0)
        return abs;
    return `${rounded > 0 ? "+" : "−"}${abs}`;
}
/** z back from GPfit points (inverse of 2·z + 5). Lossy: see profileZWithSource. */
const zFromGpfitPoints = (gpfit) => (gpfit - 5) / 2;
exports.zFromGpfitPoints = zFromGpfitPoints;
/**
 * z of a feature value for the Einzelprofil plus its source. 'backend' = the
 * stored CalculatedResult.zScore, used unchanged (no rounding). 'fromPoints'
 * = fallback when no z is stored (old or imported rows): (gpfit − 5) / 2.
 * That fallback is lossy: the points are rounded to 1 decimal (integer, even
 * floored, for rows calculated before 2025-11-20) and clamped to 1..9, so it
 * can never leave −2…+2. null = no value at all.
 */
function profileZWithSource(value) {
    if (value.z !== null && Number.isFinite(value.z)) {
        return { z: value.z, source: "backend" };
    }
    if (value.gpfit !== null && Number.isFinite(value.gpfit)) {
        return { z: (0, exports.zFromGpfitPoints)(value.gpfit), source: "fromPoints" };
    }
    return null;
}
/** z of a feature value: the stored z, else back from the points (2·z + 5). */
function profileZ(value) {
    return profileZWithSource(value)?.z ?? null;
}
/** Colour of a z-value on the GPfit points scale (bars and Farbstrahl). */
const zProfileColor = (z) => (0, gpfit_colors_1.gpfitPointColor)((0, gpfit_colors_1.zToGpfitPoints)(z));
exports.zProfileColor = zProfileColor;
/** Black or white text on a bar colour (relative luminance). */
function profileTextColor(background) {
    const c = (i) => parseInt(background.slice(i, i + 2), 16) / 255;
    const lum = 0.2126 * c(1) + 0.7152 * c(3) + 0.0722 * c(5);
    return lum < 0.4 ? "#FFFFFF" : "#111827";
}
const r1 = (n) => Math.round(n * 10) / 10;
const pts = (p) => p.map(([x, y]) => `${r1(x)},${r1(y)}`).join(" ");
/**
 * Zigzag break at the end `yEnd` of a capped bar: the peaks touch the axis
 * edge, the valleys lie `depth` inside the bar (`up` = bar grows upwards).
 */
function zProfileCap(x, width, yEnd, up, depth) {
    const s = up ? 1 : -1;
    const n = Math.max(2, Math.round(width / 8));
    const tw = width / n;
    const zigzag = [[x, yEnd + s * depth]];
    for (let i = 0; i < n; i++) {
        zigzag.push([x + (i + 0.5) * tw, yEnd]);
        zigzag.push([x + (i + 1) * tw, yEnd + s * depth]);
    }
    // White area between zigzag and the straight end (covers its outline).
    const teeth = [
        [x - 0.6, yEnd + s * depth],
        ...zigzag,
        [x + width + 0.6, yEnd + s * depth],
        [x + width + 0.6, yEnd - s],
        [x - 0.6, yEnd - s],
    ];
    return { teeth: pts(teeth), edge: pts(zigzag) };
}
/**
 * Geometry of the diverging profile: one group per feature (fixed order),
 * one bar per series (≤ 3 tests). Label placement: outside the bar end when
 * there is room up to the plot edge, otherwise inside (long bars), otherwise
 * outside anyway (text may run into the margin).
 */
function buildZProfileLayout(series, geometry) {
    const { plot } = geometry;
    const [lo, hi] = exports.Z_PROFILE_DOMAIN;
    const h = plot.bottom - plot.top;
    const yOf = (z) => plot.top + ((hi - Math.max(lo, Math.min(hi, z))) / (hi - lo)) * h;
    const zeroY = yOf(0);
    const labelLines = geometry.labelLines ?? (series.length === 1 ? 2 : 1);
    const minBar = geometry.minBar ?? 3;
    const capDepth = geometry.capDepth ?? exports.Z_PROFILE_CAP_DEPTH;
    const labelH = labelLines * geometry.labelLine + 2;
    const slot = (plot.right - plot.left) / charts_1.CHART_FEATURE_KEYS.length;
    const n = Math.max(1, series.length);
    const groupW = slot * (n === 1 ? 0.56 : 0.84);
    const gap = n === 1 ? 0 : 2;
    const barW = (groupW - gap * (n - 1)) / n;
    const groups = charts_1.CHART_FEATURE_KEYS.map((feature, fi) => {
        const cx = plot.left + (fi + 0.5) * slot;
        const bars = series.map((s, si) => {
            const x = cx - groupW / 2 + si * (barW + gap);
            const raw = s.values[feature];
            const z = raw === null || raw === undefined || !Number.isFinite(raw) ? null : raw;
            const base = {
                seriesIndex: si,
                key: s.key,
                feature,
                pattern: (0, gpfit_colors_1.chartTimepointPattern)(s.key),
                x: r1(x),
                width: r1(barW),
            };
            if (z === null) {
                return {
                    ...base,
                    z: null,
                    zSource: null,
                    rating: null,
                    color: "#D1D5DB",
                    y: r1(zeroY),
                    height: 0,
                    minHeight: false,
                    clamped: false,
                    cap: null,
                    label: null,
                };
            }
            const end = yOf(z);
            let top = Math.min(end, zeroY);
            let height = Math.abs(zeroY - end);
            const minHeight = height < minBar;
            if (minHeight) {
                height = minBar;
                top = z >= 0 ? zeroY - minBar : zeroY;
            }
            const up = z >= 0;
            const clamped = z < lo || z > hi;
            // An inside label of a capped bar starts below / above the zigzag.
            const capShift = clamped ? capDepth : 0;
            const color = (0, exports.zProfileColor)(z);
            const room = up ? top - plot.top : plot.bottom - (top + height);
            const inside = room < labelH && height >= labelH + 2;
            const firstLine = geometry.labelFont;
            let ly;
            if (up) {
                ly = inside ? top + capShift + firstLine + 1 : top - labelH + firstLine;
            }
            else {
                const bottom = top + height;
                ly = inside
                    ? bottom - capShift - labelH + firstLine
                    : bottom + firstLine + 1;
            }
            return {
                ...base,
                z,
                zSource: s.sources?.[feature] ?? null,
                rating: zRating(z),
                color,
                y: r1(top),
                height: r1(height),
                minHeight,
                clamped,
                cap: clamped
                    ? zProfileCap(x, barW, up ? top : top + height, up, capDepth)
                    : null,
                label: {
                    x: r1(x + barW / 2),
                    y: r1(ly),
                    inside,
                    color: inside ? profileTextColor(color) : "#111827",
                },
            };
        });
        return { feature, cx: r1(cx), bars };
    });
    const stops = [];
    for (let z = hi; z >= lo - 1e-9; z -= 0.125) {
        stops.push({
            offset: r1(((hi - z) / (hi - lo)) * 1000) / 1000,
            color: (0, exports.zProfileColor)(z),
        });
    }
    const latest = series.length - 1;
    const marks = groups.flatMap((g) => g.bars
        .filter((b) => b.seriesIndex === latest && b.z !== null)
        .map((b) => {
        const y = r1(yOf(b.z));
        const { x, width } = geometry.ramp;
        const out = b.z > 0 ? -capDepth : capDepth;
        return {
            y,
            feature: g.feature,
            color: b.color,
            capped: b.clamped,
            arrow: b.clamped
                ? pts([
                    [x - 1, y],
                    [x + width + 1, y],
                    [x + width / 2, y + out],
                ])
                : null,
        };
    }));
    return {
        plot,
        yOf,
        zeroY: r1(zeroY),
        ticks: exports.Z_PROFILE_TICKS.map((z) => ({
            z,
            y: r1(yOf(z)),
            rating: Math.abs(z) <= 2 ? zRating(z) : null,
        })),
        band: { top: r1(yOf(exports.Z_AVERAGE_BAND)), bottom: r1(yOf(-exports.Z_AVERAGE_BAND)) },
        ramp: {
            x: geometry.ramp.x,
            width: geometry.ramp.width,
            top: plot.top,
            bottom: plot.bottom,
            stops,
            marks,
        },
        groups,
        labelLines,
    };
}
